import { Box, Button, Checkbox, FormControlLabel, Popover, Typography } from '@mui/material';
import { ReactComponent as BlackStarsIcon } from 'assets/icons/BlackStars.svg';
import { ReactComponent as StarsIcon } from 'assets/icons/BlackStars.svg';
import { useMemo, useState } from 'react';
import { useProjectRetroContext } from '../../context/ProjectRetroContext';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import pluralize from 'pluralize';

function SummarizeLearnings({
  lessons,
  phasesWithCount,
}: {
  lessons: ArticleType[];
  phasesWithCount: Record<string, number>;
}) {
  const { companyId } = useUserData();
  const { config } = useConfigQuery({
    companyId,
    configName: CONFIGS.PROJECT_PHASES.configName,
  });
  const {
    state: { isOpenedLessonForm },
    dispatch,
  } = useProjectRetroContext();
  const options = useMemo(
    () => Object.keys(phasesWithCount).filter((option) => option !== '(blank)'),
    [phasesWithCount],
  );
  const [selectedPhases, setSelectedPhases] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleSelectAll = () => {
    setSelectedPhases((prevState) => (prevState.length === options.length ? [] : options));
  };

  const handleClick = (event) => {
    if (isOpenedLessonForm) {
      dispatch({
        payload: {
          articleIds: [],
          isOpenedLessonForm: false,
        },
      });
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedPhases([]);
  };

  const handleSumarize = () => {
    const articleIds = lessons
      ?.filter((article) => {
        return article?.projectPhases?.some((phase) => selectedPhases?.includes(phase));
      })
      .map((article) => article.id);
    handleClose();
    dispatch({
      payload: {
        articleIds,
        isOpenedLessonForm: articleIds.length > 0,
      },
    });
  };

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<StarsIcon width={18} height={18} />}
        sx={(theme) => ({
          color: theme.palette.custom.link,
          '& svg path': { fill: theme.palette.custom.link },
        })}
      >
        Summarize
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={(theme) => ({
          mt: 1,
          '& .MuiPopover-paper': {
            minWidth: 400,
            boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
          },
        })}
      >
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={(theme) => ({ fontWeight: 700, color: theme.palette.custom.activeTitle })}
            >
              Select {pluralize(config?.description || 'Phases')}
            </Typography>
            <Button
              onClick={handleSelectAll}
              variant="text"
              sx={(theme) => ({ color: theme.palette.custom.link, ml: 8 })}
            >
              {options.length === selectedPhases.length ? 'Unselect All' : 'Select All'}
            </Button>
          </Box>

          {options.map((phase) => {
            return (
              <Box>
                <FormControlLabel
                  sx={(theme) => ({ color: theme.palette.custom.activeTitle })}
                  control={
                    <Checkbox
                      checked={selectedPhases.includes(phase)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedPhases((prevState) => {
                          if (checked) {
                            return [...prevState, phase];
                          } else {
                            return prevState.filter((option) => option !== phase);
                          }
                        });
                      }}
                    />
                  }
                  label={
                    <Typography>
                      {phase} ({phasesWithCount[phase]})
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            );
          })}
          <Button
            variant="contained"
            sx={{ mt: 2, mb: 2, float: 'right' }}
            onClick={handleSumarize}
            startIcon={<BlackStarsIcon width={18} height={18} />}
            disabled={!selectedPhases.length}
          >
            Summarize
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default SummarizeLearnings;
